import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatDate, formatRelativeTime } from '@utils/format';
import { ActiveBadge } from './components/cells/ActiveBadge';
import { UiStringRowActions } from './components/cells/UiStringRowActions';
import { UiCoupledBadge } from './components/cells/UiCoupledBadge';
import type { UiString } from './schema';

export interface UiStringRowActionHandlers {
  onEdit: (row: UiString) => void;
}

// `colId` on sortable columns matches the API's sortBy key. The grid reads
// it from column state and sends it as `sortBy`. Sortable: key / active /
// updated. Other columns rely on grid-side filter only.
export function buildUiStringsColumns(
  actions: UiStringRowActionHandlers,
): ColDef<UiString>[] {
  return [
    {
      colId: 'key',
      field: 'key',
      headerName: 'Key',
      flex: 1.4,
      minWidth: 240,
      sortable: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: (p: ICellRendererParams<UiString>) =>
        p.data ? (
          <div className="flex items-center gap-2">
            <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[12px] text-foreground">
              {p.data.key}
            </code>
            {p.data.description?.toLowerCase().includes('ui-coupled') ? (
              <UiCoupledBadge />
            ) : null}
          </div>
        ) : null,
    },
    {
      field: 'namespace',
      headerName: 'NS',
      width: 130,
    },
    {
      field: 'valueEn',
      headerName: 'English',
      flex: 1.4,
      minWidth: 220,
      tooltipValueGetter: (p) => p.data?.valueEn ?? '',
    },
    {
      field: 'valueHi',
      headerName: 'Hindi',
      flex: 1.4,
      minWidth: 220,
      tooltipValueGetter: (p) => p.data?.valueHi ?? '',
      cellRenderer: (p: ICellRendererParams<UiString>) =>
        p.data ? <LocaleCell value={p.data.valueHi} /> : null,
    },
    {
      field: 'valueTe',
      headerName: 'Telugu',
      flex: 1.4,
      minWidth: 220,
      tooltipValueGetter: (p) => p.data?.valueTe ?? '',
      cellRenderer: (p: ICellRendererParams<UiString>) =>
        p.data ? <LocaleCell value={p.data.valueTe} /> : null,
    },
    {
      colId: 'active',
      field: 'isActive',
      headerName: 'Active',
      width: 120,
      sortable: true,
      cellRenderer: ActiveBadge,
    },
    {
      colId: 'updated',
      field: 'updatedAt',
      headerName: 'Updated',
      width: 200,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<UiString>) =>
        p.data ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-foreground">
              {formatDate(p.data.updatedAt)}
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-muted">
              {formatRelativeTime(p.data.updatedAt)}
            </span>
          </div>
        ) : null,
    },
    {
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<UiString>) =>
        p.data ? <UiStringRowActions row={p.data} {...actions} /> : null,
    },
  ];
}

function LocaleCell({ value }: { value: string | null }) {
  if (value === null) {
    return (
      <span className="text-[12px] italic text-foreground-muted">
        — untranslated —
      </span>
    );
  }
  return <span className="text-[13px] text-foreground">{value}</span>;
}
